import { CornerUpLeft, X } from 'lucide-react';
import { useTaskDetail } from '../../../entities/task';
import { useWorkOrderDetail } from '../../../entities/work-order';
import { Button } from '../../../shared/ui';
import { formatBoardMinutes, isWeekend } from '../../../shared/lib/schedule';
import { PLAN_LABELS, type PlanningDraftState } from '../model/draft';
import { formatPlanDay, planSummary } from '../model/planSummary';
import type { DispositionMode } from '../model/useDispositionMode';
import type { RescheduleSource } from '../model/useRescheduleSource';

interface Props {
  plan: DispositionMode;
  dayIso: string;
  /** The Termin being moved, when this is a reschedule rather than a placement. */
  source: RescheduleSource | null;
  /** Jump the board back to `source.dayIso`. Omitted when there is nowhere to jump. */
  onGoToSourceDay?: () => void;
  draft: PlanningDraftState | null;
  /** Name of the technician whose row was clicked, for the summary line. */
  pickedTechnicianName: string | null;
  /** The draft overlaps something — advisory, it does not block the button. */
  colliding: boolean;
  /**
   * The draft sits outside the technician's shift — also advisory. Saving raises the API's
   * `Außerhalb der Arbeitszeit` dialog, which offers "Trotzdem planen", so the button stays enabled.
   */
  outsideShift: boolean;
  onConfirm: () => void;
}

/**
 * The planning bar: what is being placed, where it would go, and the button that opens the form.
 *
 * Deliberately thin. It carries no duration steppers — the durations are shaped by dragging the
 * draft's handles on the board, and the modal owns the fields. The worker-side
 * `calendar-planning-overlay` has steppers because it saves directly with no form behind it.
 */
export const PlanningBar = ({
  plan,
  dayIso,
  source,
  onGoToSourceDay,
  draft,
  pickedTechnicianName,
  colliding,
  outsideShift,
  onConfirm,
}: Props) => {
  const labels = plan.mode ? PLAN_LABELS[plan.mode] : null;

  // Rescheduling names the Auftrag the same way creating does — the appointment the source hook
  // already loaded is only the detour that gets us its work order. Null in task mode: an Aufgabe
  // has no Auftrag, so this query stays disabled for the whole of it.
  const workOrderId = plan.workOrderId ?? source?.workOrderId ?? undefined;

  const workOrder = useWorkOrderDetail(workOrderId);
  const task = useTaskDetail(plan.taskId ?? undefined);

  const isRescheduling = source != null;

  /**
   * Every flag is paired with "did we actually ask for this one".
   *
   * `isPending` means *no data*, not *fetching*, so a **disabled** query reports it forever — it has
   * nothing and never will. Reading `workOrder.isPending` unguarded left the bar on its skeleton for
   * the whole of task mode, where that query is never enabled.
   */
  const isLoading =
    (source?.isPending ?? false) ||
    (workOrderId != null && workOrder.isPending) ||
    (plan.taskId != null && task.isPending);

  // The clock time the form will receive — the WORK start, i.e. after the Anfahrt.
  const slotLine =
    draft && pickedTechnicianName != null
      ? [
          pickedTechnicianName,
          formatPlanDay(dayIso),
          formatBoardMinutes(draft.startMin + draft.durations.travel),
          `${draft.durations.travel}+${draft.durations.work}+${draft.durations.departure} min`,
        ].join(' · ')
      : 'Klicken Sie in die Zeile eines Handwerkers, um einen Zeitpunkt zu wählen.';

  /**
   * Where the Termin is *today*. Stated in words rather than left to the block, because the block is
   * hidden while it is being moved and is on another day the moment the dispatcher looks around.
   */
  const currentLine =
    source && source.dayIso
      ? [formatPlanDay(source.dayIso), source.timeLabel, source.technicianName]
          .filter(Boolean)
          .join(' · ')
      : null;

  // Nowhere to jump when the board is already there, and a weekend source would only be bounced
  // back to Monday by `useDispositionDay` — so the line stays plain text and keeps telling the truth.
  const canJumpToSource =
    source != null && source.dayIso !== '' && source.dayIso !== dayIso && !isWeekend(source.dayIso);

  return (
    <div className="z-40 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
      <div className="min-w-0 flex-1">
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-56 rounded bg-gray-200" />
            <div className="h-3 w-40 rounded bg-gray-100" />
          </div>
        ) : (
          <>
            <p className="truncate font-['PT_Sans'] text-[18px] font-bold leading-tight text-[#004494]">
              {planSummary(plan, workOrder.data, task.data)}
            </p>

            {currentLine &&
              (canJumpToSource ? (
                <button
                  type="button"
                  onClick={onGoToSourceDay}
                  title="Zum aktuellen Termin springen"
                  className="mt-1 flex max-w-full items-center gap-1 rounded text-sm text-[#004494] underline-offset-2 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004494]"
                >
                  <CornerUpLeft className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
                  <span className="truncate">Aktuell: {currentLine}</span>
                </button>
              ) : (
                <p className="mt-1 truncate text-sm text-zinc-500">Aktuell: {currentLine}</p>
              ))}

            <p className="mt-1 truncate text-sm text-zinc-500">{slotLine}</p>
          </>
        )}
      </div>

      {colliding && draft && (
        <p
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
          role="alert"
        >
          Überschneidung mit einem bestehenden Eintrag
        </p>
      )}

      {/* Amber, and shown next to the red pill rather than instead of it: the two are independent
          and a draft can be both booked over and off shift. The wording is the title of the dialog
          the save will raise, so the warning and the confirmation say the same thing. */}
      {outsideShift && draft && (
        <p
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700"
          role="alert"
        >
          Außerhalb der Arbeitszeit
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button variant="primary" size="sm" fullWidth={false} disabled={!draft} onClick={onConfirm}>
          {(isRescheduling ? labels?.move : labels?.action) ?? 'Anlegen'}
        </Button>

        <button
          type="button"
          onClick={plan.exit}
          aria-label="Planung abbrechen"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#004494] transition duration-200 hover:bg-[#004494] hover:text-white"
        >
          <X className="h-6 w-6" strokeWidth={2.5} aria-hidden />
        </button>
      </div>
    </div>
  );
};
